import { supabase } from '../lib/supabase';

export const engagementMetricsService = {
  /**
   * Subscribe to real-time engagement metrics updates for a session
   * @param {string} sessionId - Session ID to monitor
   * @param {Function} onMetricUpdate - Callback function to handle new metrics
   * @returns {Object} Supabase channel for cleanup
   */
  subscribeToSessionMetrics(sessionId, onMetricUpdate) {
    const channel = supabase
      ?.channel(`session_engagement_metrics_${sessionId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'session_engagement_metrics',
          filter: `session_id=eq.${sessionId}`
        },
        (payload) => {
          if (payload?.new) {
            // Convert snake_case to camelCase
            const metric = {
              id: payload?.new?.id,
              sessionId: payload?.new?.session_id,
              userId: payload?.new?.user_id,
              helperId: payload?.new?.helper_id,
              sentimentScore: payload?.new?.sentiment_score,
              sentimentLabel: payload?.new?.sentiment_label,
              sessionDuration: payload?.new?.session_duration,
              connectionQuality: payload?.new?.connection_quality,
              connectionLatency: payload?.new?.connection_latency,
              packetLossRate: payload?.new?.packet_loss_rate,
              interactionCount: payload?.new?.interaction_count,
              messageCount: payload?.new?.message_count,
              videoQuality: payload?.new?.video_quality,
              metadata: payload?.new?.metadata,
              recordedAt: payload?.new?.recorded_at
            };
            onMetricUpdate(metric);
          }
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Get latest engagement metrics for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Latest metrics
   */
  async getLatestMetrics(sessionId) {
    try {
      const { data, error } = await supabase
        ?.from('session_engagement_metrics')
        ?.select('*')
        ?.eq('session_id', sessionId)
        ?.order('recorded_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error; // Ignore "not found" error

      if (data) {
        return {
          data: {
            id: data?.id,
            sessionId: data?.session_id,
            userId: data?.user_id,
            helperId: data?.helper_id,
            sentimentScore: data?.sentiment_score,
            sentimentLabel: data?.sentiment_label,
            sessionDuration: data?.session_duration,
            connectionQuality: data?.connection_quality,
            connectionLatency: data?.connection_latency,
            packetLossRate: data?.packet_loss_rate,
            interactionCount: data?.interaction_count,
            messageCount: data?.message_count,
            videoQuality: data?.video_quality,
            metadata: data?.metadata,
            recordedAt: data?.recorded_at
          },
          error: null
        };
      }

      return { data: null, error: null };
    } catch (error) {
      console.error('Get latest metrics error:', error);
      return { data: null, error };
    }
  },

  /**
   * Create or update engagement metrics for a session
   * @param {Object} metricsData - Metrics data to insert/update
   * @returns {Promise<Object>} Created/updated metrics
   */
  async upsertMetrics(metricsData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case
      const dbData = {
        session_id: metricsData?.sessionId,
        user_id: metricsData?.userId || user?.id,
        helper_id: metricsData?.helperId,
        sentiment_score: metricsData?.sentimentScore,
        sentiment_label: metricsData?.sentimentLabel,
        session_duration: metricsData?.sessionDuration,
        connection_quality: metricsData?.connectionQuality,
        connection_latency: metricsData?.connectionLatency,
        packet_loss_rate: metricsData?.packetLossRate,
        interaction_count: metricsData?.interactionCount,
        message_count: metricsData?.messageCount,
        video_quality: metricsData?.videoQuality,
        metadata: metricsData?.metadata || {}
      };

      const { data, error } = await supabase
        ?.from('session_engagement_metrics')
        ?.insert(dbData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Upsert metrics error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get historical metrics for a session
   * @param {string} sessionId - Session ID
   * @param {number} limit - Number of records to fetch
   * @returns {Promise<Array>} Historical metrics
   */
  async getHistoricalMetrics(sessionId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('session_engagement_metrics')
        ?.select('*')
        ?.eq('session_id', sessionId)
        ?.order('recorded_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      // Convert snake_case to camelCase
      const metrics = data?.map(metric => ({
        id: metric?.id,
        sessionId: metric?.session_id,
        userId: metric?.user_id,
        helperId: metric?.helper_id,
        sentimentScore: metric?.sentiment_score,
        sentimentLabel: metric?.sentiment_label,
        sessionDuration: metric?.session_duration,
        connectionQuality: metric?.connection_quality,
        connectionLatency: metric?.connection_latency,
        packetLossRate: metric?.packet_loss_rate,
        interactionCount: metric?.interaction_count,
        messageCount: metric?.message_count,
        videoQuality: metric?.video_quality,
        metadata: metric?.metadata,
        recordedAt: metric?.recorded_at
      })) || [];

      return { data: metrics, error: null };
    } catch (error) {
      console.error('Get historical metrics error:', error);
      return { data: [], error };
    }
  },

  /**
   * Unsubscribe from real-time updates
   * @param {Object} channel - Supabase channel to remove
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};